import React, { useState, useEffect } from 'react';
import { Download, FileText, Book, CheckCircle, Lock } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import NewsletterSection from '../components/NewsletterSection';
import { ScrollReveal } from '../components/ScrollAnimations';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResourcesPage = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadModal, setDownloadModal] = useState({ open: false, resource: null });
  const [email, setEmail] = useState('');
  const [subscribeNewsletter, setSubscribeNewsletter] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/resources`);
      const data = await response.json();
      setResources(data || []);
    } catch (error) {
      console.error('Error fetching resources:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadClick = (resource) => {
    setDownloadModal({ open: true, resource });
  };

  const handleDownload = async (e) => {
    e.preventDefault();
    if (!email || !downloadModal.resource) return;

    setDownloading(true);
    try {
      const response = await fetch(`${API_URL}/api/blog/resources/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          resource_id: downloadModal.resource.id,
          subscribe_newsletter: subscribeNewsletter
        })
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success('Téléchargement prêt !');
        // Open download link
        window.open(data.download_url, '_blank');
        setDownloadModal({ open: false, resource: null });
        setEmail('');
      } else {
        toast.error('Une erreur est survenue');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Une erreur est survenue');
    } finally {
      setDownloading(false);
    }
  };

  const getFileIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'pdf':
        return <FileText className="text-red-400" size={24} />;
      case 'ebook':
        return <Book className="text-accent" size={24} />;
      default:
        return <FileText className="text-white/60" size={24} />;
    }
  };

  return (
    <div className="bg-black min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-accent/5 to-transparent" />
        
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative">
          <ScrollReveal animation="fadeUp">
            <div className="text-center max-w-3xl mx-auto">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 text-white/60 text-sm mb-6">
                <Download size={16} className="text-accent" />
                Téléchargements gratuits
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
                Ressources <span className="text-accent">SEO</span>
              </h1>
              <p className="text-white/60 text-lg">
                Guides, checklists et templates pour booster votre référencement. 
                Téléchargez gratuitement nos ressources exclusives.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="w-12 h-12 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
            </div>
          ) : resources.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-white/60 text-lg">Aucune ressource disponible pour le moment</p>
              <p className="text-white/40 mt-2">Revenez bientôt pour découvrir nos guides SEO</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {resources.map((resource, index) => (
                <ScrollReveal key={resource.id} animation="fadeUp" delay={index * 100}>
                  <div className="group h-full bg-white/[0.02] border border-white/10 hover:border-accent/30 transition-all duration-300">
                    {/* Thumbnail */}
                    <div className="aspect-[4/3] bg-gradient-to-br from-accent/10 to-purple-500/10 relative overflow-hidden">
                      {resource.thumbnail ? (
                        <img 
                          src={resource.thumbnail.startsWith('http') ? resource.thumbnail : `${API_URL}${resource.thumbnail}`} 
                          alt={resource.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center">
                            {getFileIcon(resource.file_type)}
                          </div>
                        </div>
                      )}
                      
                      {/* Badge */}
                      <div className="absolute top-4 right-4 px-3 py-1 bg-accent text-black text-xs font-medium">
                        {resource.file_type}
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-6">
                      <span className="text-accent text-xs font-medium">{resource.category}</span>
                      <h3 className="text-lg font-semibold text-white mt-2 mb-3 group-hover:text-accent transition-colors">
                        {resource.title}
                      </h3>
                      <p className="text-white/50 text-sm mb-6 line-clamp-2">
                        {resource.description}
                      </p>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-white/40 text-xs">
                          {resource.downloads || 0} téléchargements
                        </span>
                        <button
                          onClick={() => handleDownloadClick(resource)}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-accent text-black text-sm font-medium hover:bg-accent/90 transition-colors"
                        >
                          <Download size={16} />
                          Télécharger
                        </button>
                      </div>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Download Modal */}
      {downloadModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => setDownloadModal({ open: false, resource: null })}
          />
          
          <div className="relative bg-[#0a0a0a] border border-white/10 p-8 max-w-md w-full">
            <button
              onClick={() => setDownloadModal({ open: false, resource: null })}
              className="absolute top-4 right-4 text-white/40 hover:text-white"
            >
              ✕
            </button>
            
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="text-accent" size={24} />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                Télécharger "{downloadModal.resource?.title}"
              </h3>
              <p className="text-white/60 text-sm">
                Entrez votre email pour recevoir le lien de téléchargement
              </p>
            </div>
            
            <form onSubmit={handleDownload} className="space-y-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                required
                className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-accent transition-colors"
              />
              
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={subscribeNewsletter}
                  onChange={(e) => setSubscribeNewsletter(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-accent"
                />
                <span className="text-white/60 text-sm">
                  Je souhaite recevoir les conseils SEO et les nouvelles ressources par email
                </span>
              </label>
              
              <button
                type="submit"
                disabled={downloading}
                className="w-full bg-accent text-black py-3 font-medium hover:bg-accent/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {downloading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Préparation...
                  </>
                ) : (
                  <>
                    <Download size={18} />
                    Télécharger gratuitement
                  </>
                )}
              </button>
            </form>
            
            <p className="text-white/40 text-xs text-center mt-4">
              En téléchargeant, vous acceptez notre politique de confidentialité
            </p>
          </div>
        </div>
      )}

      {/* Newsletter */}
      <NewsletterSection />

      <Footer />
    </div>
  );
};

export default ResourcesPage;
